import React, { useState } from 'react';
import styled from 'styled-components';
import ErrorShower from '../ui-utils/error-shower';

const Wrapper = styled.div`
  width: 100%;
  overflow: visible;
  margin-bottom: 16px;
  position: relative;
  max-width: 360px;
  &:focus-within::after {
    content: '';
    position: absolute;
    top: 0px;
    height: 56px;
    left: 0px;
    width: 2px;
    background: var(--primary-color);
    box-shadow: 0px 0px 10px rgba(0, 102, 255, 0.35);
    border-radius: 2px 0px 0px 2px;
  }
`;

const Input = styled.input.attrs(() => ({})) <{ paddingTop: string; blurred: boolean }>`
  width: 100%;
  height: 56px;
  background: ${(props) => (props.blurred ? 'rgba(255, 255, 255, 0.1)' : 'var(--matt-bg-color)')};
  backdrop-filter: ${(props) => (props.blurred ? 'blur(20px)' : 'initial')};
  border-radius: 2px;
  color: var(--primary-text-color);
  font-size: 12px;
  font-weight: bold;
  font-feature-settings: 'ordn' on;
  padding-left: 16px;
  padding-top: ${(props) => props.paddingTop};
  &:focus {
    box-shadow: 0px 0px 30px 10px rgba(0, 102, 255, 0.1);
    background: ${(props) => (props.blurred ? '#313946' : 'var(--matt-bg-color)')};
    border-width: 0px;
    outline: unset;
  }
  &:disabled {
    color: var(--gray-text-color);
    background: var(--matt-bg-color);
    backdrop-filter: unset;
    cursor: not-allowed;
  }
`;

const Placeholder = styled.span.attrs((props: { top: string; fontSize: string }) => ({
  top: props.top,
  fontSize: props.fontSize,
}))`
  position: absolute;
  left: 16px;
  top: ${(props) => props.top};
  color: var(--gray-text-color);
  font-weight: 500;
  font-size: ${(props) => props.fontSize};
  font-feature-settings: 'ordn' on;
  transition: 0.2s;
  pointer-events: none;
`;

const StyledInput = (props: {
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onEnterPress?: () => void;
  placeholder?: string;
  type?: 'text' | 'password' | 'email' | 'number' | 'tel';
  id?: string;
  error?: string;
  value?: string;
  disabled?: boolean;
  blurred?: boolean;
  style?: React.CSSProperties;
  min?: number;
  step?: number | string;
  autoComplete?: 'on' | 'off' | 'current-password' | 'new-password' | 'username' | 'one-time-code';
  inputMode?: 'numeric' | 'decimal';
  autoFocus?: boolean;
  uncontrolled?: boolean;
}): JSX.Element => {
  const [value, setValue] = useState(props.value || '');

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    // decimal point numbers behave wierd on different browsers, '.', '123.' fires onchange with ''
    if (props.type !== 'number' || !props.uncontrolled || (props.uncontrolled && event.currentTarget.value)) {
      setValue(event.currentTarget.value);
    }
    if (props.onChange) {
      props.onChange(event);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter') {
      if (props.onEnterPress) {
        props.onEnterPress();
      }
      event.preventDefault();
    }
  };

  return (
    <Wrapper style={props.style} className={props.blurred ? 'blurred-input' : undefined}>
      <Input
        paddingTop={props.value?.length || value.length ? '15px' : '0px'}
        type={props.type || 'text'}
        onChange={handleChange}
        value={props.uncontrolled ? undefined : (props.value || value)}
        defaultValue={props.uncontrolled ? props.value : undefined}
        id={props.id}
        disabled={props.disabled}
        blurred={!!props.blurred}
        min={props.min}
        step={props.step}
        onKeyPress={handleKeyPress}
        autoComplete={props.autoComplete}
        inputMode={props.inputMode}
        autoFocus={props.autoFocus}
      />
      <Placeholder
        fontSize={props.value?.length || value.length ? '10px' : '12px'}
        top={props.value?.length || value.length ? '10px' : '20px'}
      >
        {props.placeholder}
      </Placeholder>
      <ErrorShower error={props.error} />
    </Wrapper>
  );
};

export default StyledInput;
